import { useEffect, useRef } from "react";
import { cn } from "@/utils/cn";

interface AudioWaveformProps {
  stream: MediaStream | null;
  active: boolean;
  barCount?: number;
  className?: string;
  barClassName?: string;
}

export const AudioWaveform = ({
  stream,
  active,
  barCount = 18,
  className,
  barClassName,
}: AudioWaveformProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const barsRef = useRef<HTMLSpanElement[]>([]);
  const rafRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataRef = useRef<Uint8Array | null>(null);

  useEffect(() => {
    if (!active || !stream) return;
    const AudioCtor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtor) return;

    const ctx = new AudioCtor();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);

    ctxRef.current = ctx;
    sourceRef.current = source;
    analyserRef.current = analyser;
    dataRef.current = new Uint8Array(analyser.frequencyBinCount);

    const render = () => {
      const data = dataRef.current;
      const a = analyserRef.current;
      if (!data || !a) return;
      a.getByteFrequencyData(data);
      const step = Math.floor(data.length / barCount) || 1;
      for (let i = 0; i < barCount; i++) {
        const v = data[i * step] ?? 0;
        const pct = Math.max(8, Math.min(100, (v / 255) * 130));
        const bar = barsRef.current[i];
        if (bar) bar.style.height = `${pct}%`;
      }
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      try {
        source.disconnect();
      } catch {
        // ignore
      }
      try {
        analyser.disconnect();
      } catch {
        // ignore
      }
      ctx.close().catch(() => undefined);
      ctxRef.current = null;
      sourceRef.current = null;
      analyserRef.current = null;
      dataRef.current = null;
    };
  }, [active, stream, barCount]);

  return (
    <div
      ref={containerRef}
      className={cn("flex items-center gap-[2px] h-5", className)}
      aria-hidden
    >
      {Array.from({ length: barCount }).map((_, i) => (
        <span
          key={i}
          ref={(el) => {
            if (el) barsRef.current[i] = el;
          }}
          className={cn(
            "w-[2px] rounded-full bg-linear-to-b from-primary via-info to-primary transition-[height] duration-75",
            barClassName,
          )}
          style={{ height: "8%" }}
        />
      ))}
    </div>
  );
};
